<template>
  <div class="mass-ratio-calculation">
    <!-- 상단 입력 영역 -->
    <v-form ref="massRatioForm" lazy-validation>
      <div class="filter-box filter-box--row">
        <ul class="filter-list_free" style="width: 80%">
          <li>
            <h4>Code Grade</h4>
            <v-text-field v-model="formData.codeGrade" outlined dense readonly class="code-grade-readonly"></v-text-field>
          </li>
          <li>
            <h4>조성 계산</h4>
            <v-select v-model="formData.condition" :items="conditionOptions" item-text="text" item-value="value" outlined dense placeholder="선택"></v-select>
          </li>
        </ul>
        <div class="btn-box">
          <v-card-actions>
            <v-btn depressed class="btn-search" @click="search">검색</v-btn>
          </v-card-actions>
        </div>
      </div>
    </v-form>

    <!-- 버튼 영역 -->
    <h3 class="mt-2 mb-2 dp--row" v-show="showGrid">
      <div style="margin-left: auto; display: flex; gap: 8px">
        <v-btn depressed class="btn-excel" @click="downloadExcel">엑셀다운로드</v-btn>
        <v-btn depressed class="btn-cancel" @click="cancel">취소</v-btn>
        <v-btn depressed class="btn-save" @click="save">저장</v-btn>
      </div>
    </h3>

    <!-- 질량비 계산 그리드 영역 -->
    <div class="content-section" v-show="showGrid">
      <div class="grid-wrapper">
        <!-- ACTUAL_GENERAL용 그리드 -->
        <RealGrid v-if="formData.condition === 'ACTUAL_GENERAL'" :domName="'massRatioGrid'" :settings="generalGridSettings" ref="massRatioGrid" @click="onGridClick" key="general-grid" />
        <!-- ACTUAL_WWY용 그리드 -->
        <RealGrid v-else-if="formData.condition === 'ACTUAL_WWY'" :domName="'massRatioGridWWY'" :settings="wwyGridSettings" ref="massRatioGridWWY" @click="onGridClick" key="wwy-grid" />
        <!-- ACTUAL_LGENSOL용 그리드 -->
        <RealGrid v-else-if="formData.condition === 'ACTUAL_LGENSOL'" :domName="'massRatioGridLGENSOL'" :settings="lgensolGridSettings" ref="massRatioGridLGENSOL" @click="onGridClick" key="lgensol-grid" />
        <!-- MANUFACTURING용 그리드 -->
        <RealGrid v-else-if="formData.condition === 'MANUFACTURING'" :domName="'manufacturingGrid'" :settings="manufacturingGridSettings" ref="manufacturingGrid" @click="onGridClick" key="manufacturing-grid" />
        <!-- 기타 조건 (SELECT 등) - 그리드 없음 -->
        <div v-else class="no-grid-message">
          <p>조성 계산을 선택해주세요.</p>
        </div>
      </div>
    </div>

    <!-- 알림 모달 -->
    <SetPopup ref="confirm" />
  </div>
</template>

<script>
import RealGrid from '@/components/RealGrid.vue';
import SetPopup from '@/components/SetPopup.vue';
import { fields, columns, columnLayout, errorMessage, hideCheckBar, blankGrid } from '@/assets/grid/massRatioCalculation.js';
import { fieldsWWY, columnsWWY, columnLayoutWWY, errorMessageWWY, hideCheckBarWWY, blankGridWWY } from '@/assets/grid/massRatioCalculationWWY.js';
import { fieldsLGENSOL, columnsLGENSOL, columnLayoutLGENSOL, errorMessageLGENSOL, hideCheckBarLGENSOL, blankGridLGENSOL } from '@/assets/grid/massRatioCalculationLGENSOL.js';
import { fieldsManufacturing, columnsManufacturing, columnLayoutManufacturing, errorMessageManufacturing, hideCheckBarManufacturing, blankGridManufacturing } from '@/assets/grid/massRatioCalculationManufacturing.js';
import { getMassRatioCalculation, saveMassRatioCalculation, getRatiosBySolvent } from 'api/massRatio/massRatio.js';
import { mapMutations } from 'vuex';

export default {
  name: 'MassRatioCalculation',
  components: {
    RealGrid,
    SetPopup,
  },
  props: {
    codeGrade: {
      type: String,
      default: '',
    },
    sampleData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      formData: {
        codeGrade: this.codeGrade,
        condition: 'SELECT',
      },
      conditionOptions: [
        { text: '선택', value: 'SELECT' },
        { text: '실제조성(일반)', value: 'ACTUAL_GENERAL' },
        { text: '실제조성(w/w=Y)', value: 'ACTUAL_WWY' },
        { text: '실제조성(LG엔솔)', value: 'ACTUAL_LGENSOL' },
        { text: '제조조성', value: 'MANUFACTURING' },
      ],
      showGrid: false, // 초기에는 그리드를 숨김
      newSolvent: '', // newSolvent 값 저장
      saveData: [], // 저장할 데이터
    };
  },

  computed: {
    // ACTUAL_GENERAL용 그리드 설정
    generalGridSettings() {
      return {
        fields,
        columns,
        columnLayout, // 3단 헤더 레이아웃 복원
        errorMessage,
        hideCheckBar,
        blankGrid,
        fitStyle: 'even',
        rowHeight: 40,
        height: 1200,
        headerHeight: 108, // 3단 헤더 높이 복원 (36px * 3)
      };
    },

    // ACTUAL_WWY용 그리드 설정
    wwyGridSettings() {
      return {
        fields: fieldsWWY,
        columns: columnsWWY,
        columnLayout: columnLayoutWWY,
        errorMessage: errorMessageWWY,
        hideCheckBar: hideCheckBarWWY,
        blankGrid: blankGridWWY,
        fitStyle: 'even',
        rowHeight: 40,
        height: 1200,
        headerHeight: 108, // 3단 헤더 높이
        hideStateBar: true, // 번호 컬럼 숨기기 (LG엔솔과 동일 적용)
        stateBarWidth: 0, // 번호 컬럼 너비 0 처리
      };
    },

    // ACTUAL_LGENSOL용 그리드 설정
    lgensolGridSettings() {
      return {
        fields: fieldsLGENSOL,
        columns: columnsLGENSOL,
        columnLayout: columnLayoutLGENSOL, // 3단 헤더 레이아웃 복원
        errorMessage: errorMessageLGENSOL,
        hideCheckBar: hideCheckBarLGENSOL,
        hideStateBar: true, // 번호 컬럼 숨기기
        stateBarWidth: 0, // 번호 컬럼 너비를 0으로 설정
        blankGrid: blankGridLGENSOL,
        fitStyle: 'even',
        rowHeight: 40,
        height: 1200,
        headerHeight: 108, // 3단 헤더 높이 복원 (36px * 3)
      };
    },

    // MANUFACTURING용 그리드 설정
    manufacturingGridSettings() {
      return {
        fields: fieldsManufacturing,
        columns: columnsManufacturing,
        columnLayout: columnLayoutManufacturing, // 1단 헤더 레이아웃
        errorMessage: errorMessageManufacturing,
        hideCheckBar: hideCheckBarManufacturing,
        hideStateBar: true, // 번호 컬럼 숨기기
        stateBarWidth: 0, // 번호 컬럼 너비를 0으로 설정
        blankGrid: blankGridManufacturing,
        fitStyle: 'even',
        rowHeight: 40,
        height: 1200,
        headerHeight: 36, // 1단 헤더 높이
      };
    },

    // 현재 활성화된 그리드 참조
    currentGrid() {
      if (this.formData.condition === 'ACTUAL_LGENSOL') {
        return this.$refs.massRatioGridLGENSOL;
      } else if (this.formData.condition === 'ACTUAL_WWY') {
        return this.$refs.massRatioGridWWY;
      } else if (this.formData.condition === 'ACTUAL_GENERAL') {
        return this.$refs.massRatioGrid;
      } else if (this.formData.condition === 'MANUFACTURING') {
        return this.$refs.manufacturingGrid;
      } else {
        return null;
      }
    },
  },

  watch: {
    codeGrade: {
      handler(newVal) {
        this.formData.codeGrade = newVal;
      },
      immediate: true,
    },
    'formData.condition': {
      handler(newVal, oldVal) {
        // 조성 계산 선택값이 바뀌면 그리드를 숨김
        if (newVal !== oldVal && this.showGrid) {
          this.showGrid = false;
        }
      },
    },
  },

  methods: {
    ...mapMutations('popup', ['SET_POPUP']),

    async search() {
      // 조성 계산이 선택되지 않은 경우 경고
      if (this.formData.condition === 'SELECT') {
        this.openPopup('조성 계산을 선택해주세요.');
        return;
      }

      // codeGrade 값이 없는 경우 경고
      if (!this.formData.codeGrade || this.formData.codeGrade.trim() === '') {
        this.openPopup('Code Grade를 입력해주세요.');
        return;
      }

      try {
        // 백엔드 유효성 검사 API 호출
        const response = await getMassRatioCalculation(this.formData.codeGrade, this.formData.condition);

        if (response && response.data) {
          const { status, data } = response.data;

          // 성공적인 응답인 경우
          if (status === 200 && data && data.isValid) {
            this.openPopup(data.message || '질량비 계산이 가능합니다.');

            // newSolvent 값 저장
            this.newSolvent = data.newSolvent || '';

            // saveData 저장
            this.saveData = data.saveData || [];

            // 그리드 표시
            this.showGrid = true;

            // 그리드가 표시된 후 헤더 설정 및 데이터 로드
            this.$nextTick(() => {
              // 3단 헤더 설정 활성화
              this.setupComplexHeader();

              // calculateData를 그리드에 표시
              if (data.calculateData && data.calculateData.length > 0) {
                this.loadCalculationData(data.calculateData);
              }
            });
          } else {
            // 유효성 검사 실패
            this.openPopup(data.message || '질량비 계산이 불가능합니다.');
          }
        } else {
          this.openPopup('서버 응답이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error('질량비 계산 유효성 검사 오류:', error);

        // 에러 응답 메시지 처리
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          if (errorData.data && errorData.data.message) {
            this.openPopup(errorData.data.message);
          } else {
            this.openPopup(errorData.errorMessage || errorData.message || '서버 오류가 발생했습니다.');
          }
        } else {
          this.openPopup('네트워크 오류가 발생했습니다.');
        }
      }
    },

    close() {
      this.$emit('closeTab');
    },

    openPopup(text, closable = false, cb) {
      this.SET_POPUP({
        title: '알림',
        width: 300,
        closable: closable,
        text: text,
      });
      this.$refs.confirm.openPopup(cb);
    },

    onGridClick(data) {
      // 그리드 클릭 이벤트 핸들러
    },

    setupComplexHeader() {
      this.$nextTick(() => {
        setTimeout(() => {
          try {
            const grid = this.currentGrid;
            if (!grid || !grid.gv) {
              return;
            }

            const gv = grid.gv;

            // 헤더 높이 설정 (제조조성은 1단, 나머지는 3단)
            if (this.formData.condition === 'MANUFACTURING') {
              gv.header.height = 36; // 1단 헤더
            } else {
              gv.header.height = 108; // 3단 헤더
            }

            // 조성 계산에 따라 다른 컬럼 레이아웃 적용
            let layout;
            if (this.formData.condition === 'ACTUAL_LGENSOL') {
              layout = columnLayoutLGENSOL;
            } else if (this.formData.condition === 'ACTUAL_WWY') {
              layout = columnLayoutWWY;
            } else if (this.formData.condition === 'MANUFACTURING') {
              layout = columnLayoutManufacturing;
            } else {
              layout = columnLayout;
            }

            // 레이아웃 적용
            if (layout && grid.setGroup) {
              grid.setGroup(layout);
            }

            // 추가적인 헤더 스타일링
            this.customizeHeaderStyle(gv);
          } catch (error) {
            console.error('setupComplexHeader 오류:', error);
          }
        }, 200); // 200ms 대기
      });
    },

    // 헤더 스타일 커스터마이징
    customizeHeaderStyle(gv) {
      try {
        // LG엔솔 그리드인 경우에만 헤더 툴팁 활성화 및 셀 툴팁 설정
        if (this.formData.condition === 'ACTUAL_LGENSOL') {
          gv.setHeader({ showTooltip: true });
          // 전체 그리드 툴팁 활성화 (onShowTooltip 작동을 위해 필요)
          gv.setDisplayOptions({
            showTooltip: true,
            tooltipDelay: 500,
          });

          // 특정 셀에만 툴팁 표시하도록 설정
          gv.onShowTooltip = function (grid, index, value) {
            // Weight 컬럼(5열)의 23번째 행(itemIndex 22)에만 특별한 툴팁 적용
            if (index.column === 'Weight' && index.itemIndex === 22) {
              return 'Solvents 조성 합÷(5-1)÷1000';
            }

            // 그 외의 모든 경우는 툴팁 표시 안함
            return null;
          };
        }

        // 구분과 고객사원료명칭 컬럼에 대해 특별한 스타일 적용
        const categoryColumn = gv.columnByName('구분');
        const customerMaterialColumn = gv.columnByName('고객사원료명칭');

        if (categoryColumn) {
          categoryColumn.header.styles = {
            ...categoryColumn.header.styles,
            background: '#f8f9fa',
            fontWeight: 'bold',
          };
        }

        if (customerMaterialColumn) {
          customerMaterialColumn.header.styles = {
            ...customerMaterialColumn.header.styles,
            background: '#f8f9fa',
            fontWeight: 'bold',
          };
        }
      } catch (error) {
        console.error('헤더 스타일 적용 중 오류:', error);
      }
    },

    // 백엔드에서 받은 계산 데이터를 그리드에 로드
    loadCalculationData(calculateData) {
      try {
        const grid = this.currentGrid;
        if (!grid || !grid.dp) {
          console.error('그리드가 초기화되지 않았습니다.');
          return;
        }

        // 백엔드 데이터를 그리드 포맷에 맞게 변환
        const gridData = calculateData.map(item => {
          if (this.formData.condition === 'ACTUAL_WWY') {
            // WWY용 필드 매핑 (ACTUAL_GENERAL + 2개의 v/v Ratio 컬럼)
            return {
              구분: item.category || '',
              고객사원료명칭: item.customerMaterial || '',
              사내통용명칭: item.internalName || '',
              조성비: this.formatValue(item.composition),
              Mw: this.formatValue(item.mw),
              Density: this.formatValue(item.density),
              VVRatio1: this.formatValue(item.vvRatio1),
              VVRatio2: this.formatValue(item.vvRatio2),
              Weight: this.formatValue(item.weight),
              Volume: this.formatValue(item.volume),
              VolSolv1: this.formatValue(item.volSolv1),
              VolSolv2: this.formatValue(item.volSolv2),
              WeightSolv: this.formatValue(item.weightSolv),
              CalcWeight: this.formatValue(item.calcWeight),
              Base100_1: this.formatValue(item.base100_1),
              Base100_2: this.formatTwoDecimals(item.base100_2),
              WithAdd: this.formatTwoDecimals(item.withAdd),
              WithAddBase1: this.formatWithAddBase(item.withAddBase1),
              WithAddBase2: this.formatWithAddBase(item.withAddBase2),
              changeYn: item.changeYn || '',
            };
          } else if (this.formData.condition === 'ACTUAL_LGENSOL') {
            // LGENSOL용 필드 매핑
            return {
              구분: item.category || '',
              고객사원료명칭: item.customerMaterial || '',
              사내통용명칭: item.internalName || '',
              조성비: this.formatValue(item.composition),
              MW: this.formatValue(item.mw),
              Density: this.formatValue(item.density),
              Weight: this.formatValue(item.weight),
              Volume: this.formatValue(item.volume),
              SaltWeight: this.formatValue(item.saltWeight),
              SaltWeightPer: this.formatValue(item.saltWeightPer),
              WithAddBase1: this.formatWithAddBase(item.withAddBase1),
              WithAddBase2: this.formatWithAddBase(item.withAddBase2),
              changeYn: item.changeYn || '',
            };
          } else if (this.formData.condition === 'MANUFACTURING') {
            // 제조조성용 필드 매핑
            return {
              구분: item.category || '',
              고객사원료명칭: item.customerMaterial || '',
              사내통용명칭: item.internalName || '',
              조성비: this.formatValue(item.composition),
              실제조성: this.formatValue(item.actualComposition),
              희석용매: '',
              희석비율: this.formatValue(item.dilutionRatio),
              반제품: this.formatValue(item.semiProduct),
              반제품함량: this.formatTwoDecimals(item.semiProductContent),
              희석용매함량: this.formatTwoDecimals(item.dilutionSolventContent),
              제조조성: this.formatValue(item.manufacturingComposition),
              제조조성비: this.formatTwoDecimals(item.manufacturingCompositionRatio),
              changeYn: item.changeYn || '',
              _dilutionSolventOriginal: item.dilutionSolvent || '',
            };
          } else {
            // 기본(ACTUAL_GENERAL) 필드 매핑
            return {
              구분: item.category || '',
              고객사원료명칭: item.customerMaterial || '',
              사내통용명칭: item.internalName || '',
              조성비: this.formatValue(item.composition),
              Mw: this.formatValue(item.mw),
              Density: this.formatValue(item.density),
              Weight: this.formatValue(item.weight),
              Volume: this.formatValue(item.volume),
              VolSolv1: this.formatValue(item.volSolv1),
              VolSolv2: this.formatValue(item.volSolv2),
              WeightSolv: this.formatValue(item.weightSolv),
              CalcWeight: this.formatValue(item.calcWeight),
              Base100_1: this.formatValue(item.base100_1),
              Base100_2: this.formatTwoDecimals(item.base100_2),
              WithAdd: this.formatTwoDecimals(item.withAdd),
              WithAddBase1: this.formatWithAddBase(item.withAddBase1),
              WithAddBase2: this.formatWithAddBase(item.withAddBase2),
              changeYn: item.changeYn || '',
            };
          }
        });

        // 그리드에 데이터 설정
        grid.dp.setRows(gridData);

        // 데이터 로드 후 구분 컬럼 셀 병합 및 스타일 적용
        this.$nextTick(() => {
          const grid = this.currentGrid;
          if (grid && grid.gv) {
            const gv = grid.gv;

            // 구분 컬럼의 같은 값들을 병합
            const categoryColumn = gv.columnByName('구분');
            if (categoryColumn) {
              categoryColumn.mergeRule = { criteria: 'value' };
            }

            // 제조조성의 경우 희석용매 변경 이벤트 설정
            if (this.formData.condition === 'MANUFACTURING') {
              this.setupDilutionSolventChangeEvent(gv);
            }

            // 스타일 적용
            this.applyRowStyles(gv);

            // 제조조성의 경우: 희석용매 드롭다운의 첫 옵션을 자동 선택
            if (this.formData.condition === 'MANUFACTURING') {
              this.autoSelectFirstDilutionSolvent();
            }
          }
        });
      } catch (error) {
        console.error('계산 데이터 로드 중 오류:', error);
        this.openPopup('계산 데이터를 로드하는 중 오류가 발생했습니다.');
      }
    },

    // 각 행의 _dilutionSolventOriginal을 파싱하여 희석용매 첫 옵션 자동 선택
    async autoSelectFirstDilutionSolvent() {
      try {
        const grid = this.currentGrid;
        if (!grid || !grid.gv || !grid.dp) {
          return;
        }

        const gv = grid.gv;
        const rowCount = gv.getItemCount();

        for (let i = 0; i < rowCount; i++) {
          const rowData = grid.dp.getJsonRow(i) || {};
          const raw = rowData._dilutionSolventOriginal || '';

          // 드롭다운 옵션에서 첫 번째 값을 선택 (선택없음 제외)
          const firstValue = this.getFirstValidOption(raw);
          if (firstValue) {
            gv.setValue(i, '희석용매', firstValue);
            this.applySkyBlueRowStyle(i, true);
            // 희석비율 옵션 자동 조회 및 첫 값 자동 선택
            // 첨가제(사내통용명칭) 값을 같이 전달
            const additive = gv.getValue(i, '사내통용명칭') || '';
            try {
              if ('선택없음' !== firstValue) {
                const response = await getRatiosBySolvent(firstValue, additive);
                if (response && response.data && response.data.status === 200) {
                  const ratioOptions = response.data.data.options || [];
                  this.setDilutionRatioOptions(i, ratioOptions, true);
                }
              }
            } catch (e) {
              console.error('자동 희석비율 옵션 조회 오류:', e);
            }
          }
        }

        gv.refresh();
        try {
          this.setManufacturingCompositionRatioBySolventAggregation();
          this.updateManufacturingSummary();
          gv.refresh();
        } catch (_) {}
      } catch (e) {
        console.error('희석용매 자동 선택 중 오류:', e);
      }
    },

    // 드롭다운 옵션에서 "선택없음"을 제외한 첫 번째 값을 반환
    getFirstValidOption(dilutionSolventStr) {
      try {
        if (!dilutionSolventStr || dilutionSolventStr === '[]') return '';
        const matches = dilutionSolventStr.match(/\{([^}]+)\}/g);
        if (!matches || matches.length === 0) return '';

        // 파싱하여 [ { value, label } ] 목록으로 변환
        const options = [];
        for (let m of matches) {
          const content = m.slice(1, -1);
          if (content.includes('=')) {
            const parts = content.split('=', 2);
            options.push({ value: (parts[0] || '').trim(), label: (parts[1] || '').trim() });
          } else {
            const clean = content.trim();
            options.push({ value: clean, label: clean });
          }
        }

        // 첫 번째 옵션 선택 (선택없음 상관없이)
        if (options.length > 0) {
          return options[0].value;
        }
      } catch (e) {
        return '';
      }
    },

    // 숫자 값 포맷팅 (null, undefined 처리)
    formatValue(value) {
      if (value === null || value === undefined) {
        return '';
      }

      // 숫자인 경우 문자열로 변환
      if (typeof value === 'number') {
        return value.toString();
      }

      return value;
    },

    // WithAddBase1/2 전용 포맷터: 소수점 한자리면 두자리로 패딩(예: 0.1 -> 0.10), 정수도 두자리(예: 1 -> 1.00), 그 외는 원본 유지
    formatWithAddBase(value) {
      if (value === null || value === undefined) {
        return '';
      }
      const text = String(value).trim();
      if (text === '') return '';

      const clean = text.replace(/,/g, '');
      if (!/^-?\d+(?:\.\d+)?$/.test(clean)) {
        return text; // 숫자가 아니면 원본 유지
      }

      const num = parseFloat(clean);
      if (!Number.isFinite(num)) return text;

      if (clean.includes('.')) {
        const decimalPart = clean.split('.')[1] || '';
        if (decimalPart.length === 0) {
          // '1.' 같은 형태 방어
          return num.toFixed(2);
        }
        if (decimalPart.length === 1) {
          // 한 자리면 두 자리로 패딩
          return num.toFixed(2);
        }
        // 두 자리 이상이면 원본 유지
        return text;
      }

      // 정수인 경우 두 자리로
      return num.toFixed(2);
    },

    // 공통 2자리 포맷터: 정수 및 소수 한자리는 두자리로, 두 자리 이상은 원본 유지
    formatTwoDecimals(value) {
      if (value === null || value === undefined) {
        return '';
      }
      const text = String(value).trim();
      if (text === '') return '';

      const clean = text.replace(/,/g, '');
      if (!/^-?\d+(?:\.\d+)?$/.test(clean)) {
        return text;
      }

      const num = parseFloat(clean);
      if (!Number.isFinite(num)) return text;

      if (clean.includes('.')) {
        const decimalPart = clean.split('.')[1] || '';
        if (decimalPart.length <= 1) {
          return num.toFixed(2);
        }
        return text;
      }
      return num.toFixed(2);
    },

    // 항상 소수점 둘째 자리까지 반올림해 문자열로 반환
    formatFixedTwo(value) {
      if (value === null || value === undefined) return '';
      const num = parseFloat(String(value).replace(/,/g, ''));
      if (!Number.isFinite(num)) return '';
      return num.toFixed(2);
    },

    // 구분 값에 따른 행 스타일 적용
    applyRowStyles(gv) {
      try {
        // 행 스타일 콜백
        gv.setRowStyleCallback(function (grid, item, fixed) {
          var categoryValue = grid.getValue(item.index, '구분');

          if (categoryValue == 'Solvent') {
            return 'rg-solvent-row';
          } else if (categoryValue == 'Salt') {
            return 'rg-salt-row';
          } else if (categoryValue == 'Additive') {
            return 'rg-additive-row';
          } else if (categoryValue == '합계') {
            return 'rg-summary-row';
          }
        });

        // changeYn이 'Y'인 WithAdd 컬럼과 newSolvent가 'N'인 경우 (3)~(11) 컬럼에 스타일 적용
        this.applyCellStyles(gv);
      } catch (error) {
        console.error('행 스타일 적용 중 오류:', error);
      }
    },

    // 희석용매 변경 이벤트 설정
    setupDilutionSolventChangeEvent(gv) {
      try {
        // 사용자 입력이 셀에 반영될 때 발생하는 이벤트
        gv.onEditCommit = (grid, index, oldValue, newValue) => {
          console.log('onEditCommit - field:', index.fieldName, 'oldValue:', oldValue, 'newValue:', newValue);

          if (index.fieldName === '희석용매') {
            console.log('희석용매 값이 변경되었습니다:', oldValue, '->', newValue);
            // 희석용매가 변경되면 해당 행의 희석비율 컬럼 상태도 업데이트
            const isReset = newValue === '선택없음' || newValue === '' || newValue == null;
            if (!isReset) {
              // 희석용매 값이 있는 경우 API 호출
              this.handleDilutionSolventChange(newValue, index.itemIndex);
            } else {
              // 희석용매가 '선택없음' 계열로 변경된 경우(N 또는 빈값)
              // 희석용매 값을 명시적으로 'N'으로 정규화하고 희석비율도 'N'으로 설정
              grid.setValue(index.itemIndex, '희석용매', '선택없음');
              grid.setValue(index.itemIndex, '희석비율', '선택없음');
              // 비율이 리셋되었으므로 희석용매함량도 비움
              grid.setValue(index.itemIndex, '희석용매함량', '');
              // 비율이 유효하지 않으면 파생 칼럼도 초기화
              grid.setValue(index.itemIndex, '반제품', '');
              grid.setValue(index.itemIndex, '반제품함량', '');
              // 제조조성에는 고객사원료명칭, 제조조성비에는 실제조성 값을 반영
              const materialWhenReset = grid.getValue(index.itemIndex, '고객사원료명칭') || '';
              const actualWhenReset = grid.getValue(index.itemIndex, '실제조성');
              console.log(materialWhenReset);
              grid.setValue(index.itemIndex, '제조조성', materialWhenReset);
              grid.setValue(index.itemIndex, '제조조성비', actualWhenReset == null ? '' : this.formatFixedTwo(actualWhenReset));
              // 선택없음으로 변경되었으므로 해당 용매 행의 제조조성비를 실제조성으로 복원
              try {
                const prevRaw = (oldValue == null ? '' : String(oldValue)).trim();
                if (prevRaw && prevRaw !== '선택없음') {
                  const names =
                    prevRaw.indexOf(':') >= 0
                      ? prevRaw
                          .split(':')
                          .map(s => (s || '').trim())
                          .filter(Boolean)
                      : [prevRaw];
                  const gvRef = grid.gv || grid;
                  const rowCount = gvRef.getItemCount ? gvRef.getItemCount() : 0;
                  names.forEach(name => {
                    for (let i = 0; i < rowCount; i++) {
                      const category = gvRef.getValue(i, '구분');
                      const material = gvRef.getValue(i, '고객사원료명칭');
                      if (category === 'Solvent' && String(material || '').trim() === name) {
                        const actualRestore = gvRef.getValue(i, '실제조성');
                        gvRef.setValue(i, '제조조성비', actualRestore == null ? '' : this.formatTwoDecimals(actualRestore));
                        break;
                      }
                    }
                    // 선택없음 시 스타일 제거(복수 솔벤트 지원)
                    this.setSolventManufacturingCellStyleByName(name, false);
                  });
                }
              } catch (e) {
                console.error('선택없음 복원 처리 오류:', e);
              }
              gv.commit();
              // 선택된 희석용매 행의 빨간색 스타일 제거(복수 솔벤트 지원)
              try {
                const prevRaw2 = (oldValue == null ? '' : String(oldValue)).trim();
                const names2 =
                  prevRaw2.indexOf(':') >= 0
                    ? prevRaw2
                        .split(':')
                        .map(s => (s || '').trim())
                        .filter(Boolean)
                    : [prevRaw2];
                names2.forEach(n => this.setSolventManufacturingCellStyleByName(n, false));
              } catch (_) {}
              // 전체 집계 방식으로 Solvent 제조조성비 재계산
              this.setManufacturingCompositionRatioBySolventAggregation();
              this.updateManufacturingSummary();
              // 새로고침
              grid.refresh();
            }
          } else if (index.fieldName === '희석비율') {
            // 희석비율 변경 시 값 강제 반영(라벨→값 정규화) 후 희석용매함량 재계산
            const normalized = this.normalizeRatioDomainValue(index.itemIndex, newValue);
            grid.setValue(index.itemIndex, '희석비율', normalized);
            this.onDilutionRatioChanged(index.itemIndex, normalized);
          }
        };

        // 그리드의 포커스 셀의 위치가 변경된 후 발생하는 이벤트
        gv.onCurrentChanged = (grid, newIndex) => {
          if (newIndex && newIndex.dataRow > -1) {
            const column = grid.columnByName ? grid.columnByName(newIndex.column) : null;
            const fieldName = column ? column.fieldName : newIndex.column;
            if (fieldName === '희석용매') {
              const keyValue = grid.getValue(newIndex.itemIndex, '희석용매');
              console.log('희석용매 셀로 포커스 이동:', keyValue);
            }
          }
        };

        // 에디터 표시 제어 (newSolvent 조건만 처리)
        gv.onShowEditor = (grid, index, props) => {
          console.log('onShowEditor 호출:', {
            fieldName: index.fieldName,
            itemIndex: index.itemIndex,
            dataRow: index.dataRow,
            newSolvent: this.newSolvent,
          });

          // newSolvent가 'N'인 경우 모든 컬럼 편집 불가
          if (this.newSolvent === 'N') {
            console.log('newSolvent가 N이므로 편집 불가:', index.fieldName, index.itemIndex);
            return false;
          }

          return true; // 기본 동작 (희석비율은 styleCallback에서 제어)
        };

        // 에디터가 닫힐 때 희석비율 컬럼을 원래 상태로 되돌리기 (불필요하므로 제거)
        gv.onHideEditor = null;
      } catch (error) {
        console.error('희석용매 변경 이벤트 설정 중 오류:', error);
      }
    },

    // NEW: 희석비율 에디터가 넘겨준 값(라벨 또는 값)을 도메인 값으로 정규화
    normalizeRatioDomainValue(rowIndex, rawValue) {
      try {
        const grid = this.currentGrid;
        if (!grid || !grid.gv || !grid.dp) return rawValue == null ? '' : String(rawValue);
        const dataRow = grid.gv.getDataRow(rowIndex);
        if (dataRow == null || dataRow < 0) return rawValue == null ? '' : String(rawValue);
        const rowData = grid.dp.getJsonRow(dataRow);
        const values = rowData && rowData._dilutionRatioValues ? (Array.isArray(rowData._dilutionRatioValues) ? rowData._dilutionRatioValues : JSON.parse(rowData._dilutionRatioValues)) : [];
        const labels = rowData && rowData._dilutionRatioLabels ? (Array.isArray(rowData._dilutionRatioLabels) ? rowData._dilutionRatioLabels : JSON.parse(rowData._dilutionRatioLabels)) : [];
        const text = rawValue == null ? '' : String(rawValue);
        // 1) 이미 값 배열에 존재하면 그대로 사용
        const vIdx = values.indexOf(text);
        if (vIdx >= 0) return values[vIdx];
        // 2) 라벨 배열에서 찾으면 해당 값으로 매핑
        const lIdx = labels.indexOf(text);
        if (lIdx >= 0) return values[lIdx] || text;
        // 3) 매칭 실패시 원본 텍스트 반환
        return text;
      } catch (e) {
        return rawValue == null ? '' : String(rawValue);
      }
    },

    // 희석비율 드롭다운 옵션 설정 (autoSelect: 첫 옵션 자동 선택 여부)
    setDilutionRatioOptions(rowIndex, ratioOptions, autoSelect = true) {
      try {
        const grid = this.currentGrid;
        if (!grid || !grid.gv || !grid.dp) {
          console.error('그리드가 초기화되지 않았습니다.');
          return;
        }

        // 백엔드 응답 옵션을 드롭다운 형태로 변환
        const values = ratioOptions.map(option => option.value || option);
        const labels = ratioOptions.map(option => option.label || option.text || option);

        console.log('설정할 희석비율 옵션:', { values, labels, rowIndex });

        // 해당 행의 데이터에 드롭다운 옵션 정보 저장 (JSON 문자열로 저장)
        const currentData = grid.dp.getJsonRow(rowIndex);
        if (currentData) {
          currentData._dilutionRatioValues = JSON.stringify(values);
          currentData._dilutionRatioLabels = JSON.stringify(labels);
          grid.dp.updateRow(rowIndex, currentData);
        }

        // 옵션이 존재하는 행은 하늘색으로 표시
        // try {
        //   const hasOptions = Array.isArray(values) && values.length > 0;
        //   this.applySkyBlueRowStyle(rowIndex, hasOptions);
        // } catch (e) {
        //   console.error('하늘색 행 스타일 적용 오류:', e);
        // }

        // 첫 번째 유효 옵션을 자동 선택
        if (autoSelect) {
          let selected = '';
          for (let i = 0; i < values.length; i++) {
            const v = (values[i] == null ? '' : String(values[i])).trim();
            if (v !== '') {
              selected = v;
              break;
            }
          }
          if (selected) {
            grid.gv.setValue(rowIndex, '희석비율', selected);
            grid.gv.commit();
            grid.gv.refresh();
            // 프로그램적으로 설정된 경우도 희석용매함량 재계산
            this.onDilutionRatioChanged(rowIndex, selected);
            // 집계 방식으로 제조조성비 재계산 및 합계 갱신
            try {
              this.setManufacturingCompositionRatioBySolventAggregation();
              this.updateManufacturingSummary();
              grid.gv.refresh();
            } catch (_) {}
          }
        }

        // 간단한 그리드 새로고침
        grid.gv.refresh();

        console.log('희석비율 드롭다운 옵션이 설정되었습니다.');
      } catch (error) {
        console.error('희석비율 드롭다운 옵션 설정 중 오류:', error);
      }
    },

    // 희석용매 값 변경 처리
    async handleDilutionSolventChange(solventValue, rowIndex) {
      try {
        console.log('handleDilutionSolventChange 호출:', solventValue, 'rowIndex:', rowIndex);

        if (!solventValue || solventValue.trim() === '') {
          console.log('희석용매 값이 비어있습니다.');
          return;
        }

        // 해당 행의 첨가제(사내통용명칭) 값 가져오기
        const grid = this.currentGrid;
        const additive = grid && grid.gv ? grid.gv.getValue(rowIndex, '사내통용명칭') : '';

        console.log('백엔드 API 호출 시작 - solvent:', solventValue, 'additive:', additive);

        // 백엔드 API 호출 (첨가제 정보 포함)
        const response = await getRatiosBySolvent(solventValue, additive);

        console.log('백엔드 응답:', response);

        if (response && response.data && response.data.status === 200) {
          const ratioOptions = response.data.data.options || [];
          console.log('희석비율 옵션:', ratioOptions);

          // 해당 행의 희석비율 드롭다운 옵션을 동적으로 설정
          this.setDilutionRatioOptions(rowIndex, ratioOptions);
        } else {
          console.error('희석비율 옵션 조회 실패:', response);
        }
      } catch (error) {
        console.error('희석용매 변경 처리 중 오류:', error);
      }
    },

    // NEW: 희석비율 변경 시 희석용매함량 업데이트
    onDilutionRatioChanged(rowIndex, ratioValue) {
      try {
        if (this.formData.condition !== 'MANUFACTURING') return;
        const grid = this.currentGrid;
        if (!grid || !grid.gv) return;

        const ratioStr = (ratioValue == null ? '' : String(ratioValue)).trim();
        // 실제조성 값을 숫자로 파싱
        const actualRaw = grid.gv.getValue(rowIndex, '실제조성');
        const actual = parseFloat((actualRaw == null ? '' : String(actualRaw)).replace(/,/g, ''));
        if (!isFinite(actual)) {
          grid.gv.setValue(rowIndex, '희석용매함량', '');
          grid.gv.setValue(rowIndex, '반제품', '');
          grid.gv.setValue(rowIndex, '반제품함량', '');
          grid.gv.setValue(rowIndex, '제조조성', '');
          grid.gv.setValue(rowIndex, '제조조성비', '');
          grid.gv.refresh();
          return;
        }

        // ':' 개수에 따라 계산 방식 분기
        const colonCount = (ratioStr.match(/:/g) || []).length;

        // 유효하지 않은 비율이면 공백 처리
        let displayed = '';
        let isValidRatio = false;
        if (colonCount === 2) {
          // a:b:c → (b+c) × 실제조성비
          const { a, b, c } = this.parseRatioThree(ratioStr);
          if (isFinite(a) && isFinite(b) && isFinite(c)) {
            const calculated = (b + c) * actual;
            displayed = Number.isFinite(calculated) ? calculated.toFixed(2) : '';
            isValidRatio = true;
          }
        } else {
          // a:b → (b/a) × 실제조성비
          const { a, b } = this.parseRatio(ratioStr);
          if (isFinite(a) && isFinite(b)) {
            const calculated = (b / a) * actual;
            displayed = Number.isFinite(calculated) ? calculated.toFixed(2) : '';
            isValidRatio = true;
          }
        }

        if (!isValidRatio) {
          grid.gv.setValue(rowIndex, '희석용매함량', '');
          // 비율이 유효하지 않으면 파생 칼럼도 초기화
          grid.gv.setValue(rowIndex, '반제품', '');
          grid.gv.setValue(rowIndex, '반제품함량', '');
          // 제조조성에는 고객사원료명칭, 제조조성비에는 실제조성 값을 반영
          const material = grid.gv.getValue(rowIndex, '고객사원료명칭') || '';
          const actualRawForInvalid = grid.gv.getValue(rowIndex, '실제조성');
          grid.gv.setValue(rowIndex, '제조조성', material);
          grid.gv.setValue(rowIndex, '제조조성비', actualRawForInvalid == null ? '' : this.formatTwoDecimals(actualRawForInvalid));
          // 같은 용매명의 Solvent 행 제조조성비 복원(=실제조성)
          try {
            const solventRaw = (grid.gv.getValue(rowIndex, '희석용매') || '').trim();
            if (solventRaw && solventRaw !== '선택없음') {
              const names =
                solventRaw.indexOf(':') >= 0
                  ? solventRaw
                      .split(':')
                      .map(s => (s || '').trim())
                      .filter(Boolean)
                  : [solventRaw];
              const gv = grid.gv;
              const rowCount = gv.getItemCount();
              names.forEach(name => {
                for (let i = 0; i < rowCount; i++) {
                  const category = gv.getValue(i, '구분');
                  const materialName = gv.getValue(i, '고객사원료명칭');
                  if (category === 'Solvent' && String(materialName || '').trim() === name) {
                    const actualRestore = gv.getValue(i, '실제조성');
                    gv.setValue(i, '제조조성비', actualRestore == null ? '' : this.formatTwoDecimals(actualRestore));
                    break;
                  }
                }
              });
            }
          } catch (e) {
            console.error('비율 선택없음 복원 처리 오류:', e);
          }
          // 마지막으로 빨간색 적용했던 솔벤트의 스타일 제거
          try {
            const dp = grid.dp;
            const rowData = dp && dp.getJsonRow ? dp.getJsonRow(rowIndex) || {} : {};
            const lastStyled = (rowData.희석용매 || '').trim();
            console.log('setSolventManufacturingCellStyleByName');
            if (lastStyled) {
              const names =
                lastStyled.indexOf(':') >= 0
                  ? lastStyled
                      .split(':')
                      .map(s => (s || '').trim())
                      .filter(Boolean)
                  : [lastStyled];
              names.forEach(n => this.setSolventManufacturingCellStyleByName(n, false));
              rowData._lastStyledSolventName = '';
              dp && dp.updateRow && dp.updateRow(rowIndex, rowData);
            }
          } catch (e) {
            console.error('빨간색 스타일 제거 오류:', e);
          }
          // 전체 집계 방식으로 Solvent 제조조성비 재계산
          this.setManufacturingCompositionRatioBySolventAggregation();
          this.updateManufacturingSummary();
          grid.gv.refresh();
          return;
        }

        // 계산: a:b는 (b ÷ a × 실제조성비), a:b:c는 ((b + c) × 실제조성비). 소수점 셋째자리에서 반올림하여 둘째자리까지 표기
        grid.gv.setValue(rowIndex, '희석용매함량', this.formatTwoDecimals(displayed));

        // 추가 규칙:
        // - 반제품 = 고객사원료명칭 + 희석용매(문자열 결합) [':' 1개]
        // - 반제품 = 고객사원료명칭 + 'Solution' [':' 2개]
        const material = grid.gv.getValue(rowIndex, '사내통용명칭') || '';
        const solvent = grid.gv.getValue(rowIndex, '희석용매') || '';
        let semiProductText = '';
        if (colonCount === 2) {
          semiProductText = `${material} Solution`;
        } else {
          semiProductText = `${material}${solvent ? ' ' + solvent : ''}`;
        }
        grid.gv.setValue(rowIndex, '반제품', semiProductText);

        // - 반제품함량 = 실제조성 + 희석용매함량
        const solventAmount = parseFloat(displayed);
        const semiContent = (isFinite(actual) ? actual : 0) + (isFinite(solventAmount) ? solventAmount : 0);
        grid.gv.setValue(rowIndex, '반제품함량', isFinite(semiContent) ? this.formatFixedTwo(semiContent) : '');

        // 반제품, 반제품함량이 계산 완료되면 제조조성/제조조성비에 동일하게 반영
        if (semiProductText && isFinite(semiContent)) {
          grid.gv.setValue(rowIndex, '제조조성', semiProductText);
          grid.gv.setValue(rowIndex, '제조조성비', this.formatFixedTwo(semiContent));
        } else {
          grid.gv.setValue(rowIndex, '제조조성', '');
          grid.gv.setValue(rowIndex, '제조조성비', '');
        }

        // 전체 집계 방식으로 Solvent 제조조성비 재계산
        this.setManufacturingCompositionRatioBySolventAggregation();

        // 합계 라인 제조조성비 갱신
        this.updateManufacturingSummary();

        grid.gv.refresh();
      } catch (e) {
        console.error('희석비율 변경 처리 중 오류:', e);
      }
    },

    // NEW: 제조조성비 합계를 계산하여 마지막(합계) 행에 반영
    updateManufacturingSummary() {
      try {
        if (this.formData.condition !== 'MANUFACTURING') return;
        const grid = this.currentGrid;
        if (!grid || !grid.gv) return;
        const gv = grid.gv;
        const rowCount = gv.getItemCount();
        if (rowCount <= 0) return;

        // 마지막 행을 합계 행으로 가정, 첫 행부터 마지막-1까지 합산 (부동소수 오차 방지 위해 100배 정수 합산)
        let sumTimes100 = 0;
        for (let i = 0; i < rowCount - 1; i++) {
          const valRaw = gv.getValue(i, '제조조성비');
          const val = parseFloat((valRaw == null ? '' : String(valRaw)).replace(/,/g, ''));
          if (isFinite(val)) sumTimes100 += Math.round(val * 100);
        }
        const sum = sumTimes100 / 100;

        // 합계 행(마지막 행)에 설정(항상 소수점 둘째 자리까지)
        const summaryIndex = rowCount - 1;
        gv.setValue(summaryIndex, '제조조성비', this.formatFixedTwo(sum));
      } catch (e) {
        console.error('제조조성 합계 갱신 중 오류:', e);
      }
    },

    // NEW: "a:b" 형태의 문자열을 파싱하여 a, b를 숫자로 반환
    parseRatio(ratioStr) {
      try {
        if (!ratioStr || ratioStr === '선택없음') return { a: NaN, b: NaN };
        const parts = String(ratioStr).split(':');
        if (parts.length !== 2) return { a: NaN, b: NaN };
        const a = parseFloat(parts[0]);
        const b = parseFloat(parts[1]);
        if (!isFinite(a) || a === 0 || !isFinite(b)) return { a: NaN, b: NaN };
        return { a, b };
      } catch (_) {
        return { a: NaN, b: NaN };
      }
    },

    // NEW: "a:b:c" 형태의 문자열을 파싱하여 a, b, c를 숫자로 반환
    parseRatioThree(ratioStr) {
      try {
        if (!ratioStr || ratioStr === '선택없음') return { a: NaN, b: NaN, c: NaN };
        const parts = String(ratioStr).split(':');
        if (parts.length !== 3) return { a: NaN, b: NaN, c: NaN };
        const a = parseFloat(parts[0]);
        const b = parseFloat(parts[1]);
        const c = parseFloat(parts[2]);
        if (!isFinite(a) || !isFinite(b) || !isFinite(c)) return { a: NaN, b: NaN, c: NaN };
        return { a, b, c };
      } catch (_) {
        return { a: NaN, b: NaN, c: NaN };
      }
    },

    // NEW: 전체 Solvent 행을 순회하여 제조조성비를 집계 방식으로 설정한다
    // 규칙
    // - a:b   → 단일 솔벤트인 경우 차감량 = 해당 행의 '희석용매함량'
    // - a:b:c → 희석용매 "X:Y" 에서 X에는 (b/a) × 실제조성, Y에는 (c/a) × 실제조성 차감
    setManufacturingCompositionRatioBySolventAggregation() {
      try {
        if (this.formData.condition !== 'MANUFACTURING') return;
        const grid = this.currentGrid;
        if (!grid || !grid.gv) return;
        const gv = grid.gv;
        const rowCount = gv.getItemCount();

        // 1) Solvent 행 수집
        const solventRows = [];
        for (let i = 0; i < rowCount; i++) {
          const category = gv.getValue(i, '구분');
          if (category === 'Solvent') {
            const name = (gv.getValue(i, '고객사원료명칭') || '').trim();
            const actualRaw = gv.getValue(i, '실제조성');
            const actual = parseFloat((actualRaw == null ? '' : String(actualRaw)).replace(/,/g, ''));
            solventRows.push({ index: i, name, actual: isFinite(actual) ? actual : 0 });
          }
        }

        // 2) 각 Solvent에 대해 Salt/Additive 전 행을 스캔하여 차감 합산
        for (const solvent of solventRows) {
          let sumDeduction = 0;
          for (let i = 0; i < rowCount; i++) {
            const category = gv.getValue(i, '구분');
            if (category !== 'Additive' && category !== 'Salt') continue;

            const actualRaw = gv.getValue(i, '실제조성');
            const actual = parseFloat((actualRaw == null ? '' : String(actualRaw)).replace(/,/g, ''));
            if (!isFinite(actual)) continue;

            const ratioStr = (gv.getValue(i, '희석비율') == null ? '' : String(gv.getValue(i, '희석비율'))).trim();
            if (!ratioStr || ratioStr === '선택없음') continue;

            const solventStr = (gv.getValue(i, '희석용매') == null ? '' : String(gv.getValue(i, '희석용매'))).trim();
            if (!solventStr) continue;

            const colonCount = (ratioStr.match(/:/g) || []).length;
            if (colonCount === 2 && solventStr.indexOf(':') >= 0) {
              const { a, b, c } = this.parseRatioThree(ratioStr);
              const names = solventStr
                .split(':')
                .map(s => (s || '').trim())
                .filter(Boolean);
              const ratioBase = isFinite(a) && a !== 0 ? a : NaN;
              if (names[0] && names[0] === solvent.name && isFinite(b) && isFinite(ratioBase)) sumDeduction += (b / ratioBase) * actual;
              if (names[1] && names[1] === solvent.name && isFinite(c) && isFinite(ratioBase)) sumDeduction += (c / ratioBase) * actual;
            } else {
              // 단일 솔벤트 (a:b)
              if (solventStr === solvent.name) {
                const solventAmountRaw = gv.getValue(i, '희석용매함량');
                const solventAmount = parseFloat((solventAmountRaw == null ? '' : String(solventAmountRaw)).replace(/,/g, ''));
                if (isFinite(solventAmount)) sumDeduction += solventAmount;
              }
            }
          }

          // 3) 제조조성비 = 실제조성 - 합산 차감
          const next = (isFinite(solvent.actual) ? solvent.actual : 0) - (isFinite(sumDeduction) ? sumDeduction : 0);
          gv.setValue(solvent.index, '제조조성비', isFinite(next) ? this.formatFixedTwo(next) : '');
          // 차감 존재 여부에 따라 스타일 지정
          this.setSolventManufacturingCellStyleByName(solvent.name, isFinite(sumDeduction) && sumDeduction > 0);
        }
      } catch (e) {
        console.error('제조조성비 집계 설정 중 오류:', e);
      }
    },

    // 개별 셀에 스타일 적용
    applyCellStyles(gv) {
      try {
        const rowCount = gv.getItemCount();

        for (let i = 0; i < rowCount; i++) {
          // changeYn이 'Y'인 경우 WithAdd 컬럼을 빨간색으로
          const changeYn = gv.getValue(i, 'changeYn');
          if (changeYn === 'Y') {
            gv.addUserCellStyle(i, 'WithAddBase2', {
              style: { background: '#ff0000', color: '#ffffff' },
            });
          }

          // newSolvent가 'N'인 경우 해당 컬럼을 합계 색깔로
          if (this.newSolvent === 'N') {
            let columnsToStyle;
            if (this.formData.condition === 'ACTUAL_LGENSOL') {
              // LGENSOL의 경우 (3)~(8) 컬럼
              columnsToStyle = ['MW', 'Density', 'Weight', 'Volume', 'SaltWeight', 'SaltWeightPer'];
            } else if (this.formData.condition === 'ACTUAL_WWY') {
              // WWY의 경우 (3)~(13) 컬럼
              columnsToStyle = ['Mw', 'Density', 'VVRatio1', 'VVRatio2', 'Weight', 'Volume', 'VolSolv1', 'VolSolv2', 'WeightSolv', 'CalcWeight', 'Base100_1'];
            } else if (this.formData.condition === 'ACTUAL_GENERAL') {
              // 기본(ACTUAL_GENERAL)의 경우 (3)~(11) 컬럼
              columnsToStyle = ['Mw', 'Density', 'Weight', 'Volume', 'VolSolv1', 'VolSolv2', 'WeightSolv', 'CalcWeight', 'Base100_1'];
            } else {
              columnsToStyle = ['희석용매', '희석비율', '반제품', '반제품함량', '희석용매함량'];
            }

            columnsToStyle.forEach(fieldName => {
              gv.addUserCellStyle(i, fieldName, {
                style: { background: '#f5f5f5', color: '#333333', fontWeight: 'bold' },
              });
            });
          }
        }

        gv.refresh();
      } catch (error) {
        console.error('셀 스타일 적용 중 오류:', error);
      }
    },

    // 취소 버튼 클릭
    cancel() {
      // 탭 닫기
      this.close();
    },

    // 엑셀 다운로드
    downloadExcel() {
      try {
        const grid = this.currentGrid;
        if (!grid || !grid.gv) {
          this.openPopup('내보낼 그리드가 없습니다.');
          return;
        }
        const gv = grid.gv;
        const fileName = `${this.formData.codeGrade || 'MassRatio'}_${this.formData.condition || 'EXPORT'}.xlsx`;
        if (typeof gv.exportGrid === 'function') {
          gv.exportGrid({
            type: 'excel',
            target: 'local',
            fileName: fileName,
            showProgress: true,
            progressMessage: '엑셀 Export중입니다.',
            indicator: 'default',
            header: 'default',
            footer: 'default',
            compatibility: true,
            done: function () {
              alert('엑셀 다운로드가 완료되었습니다.');
            },
          });
        } else {
          this.openPopup('엑셀 내보내기를 지원하지 않는 그리드입니다.');
        }
      } catch (e) {
        console.error('엑셀 다운로드 중 오류:', e);
        this.openPopup('엑셀 다운로드 중 오류가 발생했습니다.');
      }
    },

    // 저장 버튼 클릭
    async save() {
      try {
        // 저장할 데이터 검증
        if (!this.saveData || this.saveData.length === 0) {
          this.openPopup('저장할 데이터가 없습니다.');
          return;
        }

        if (!this.formData.codeGrade || this.formData.codeGrade.trim() === '') {
          this.openPopup('Code Grade가 없습니다.');
          return;
        }

        // 제조조성일 때: 그리드의 제조조성비 값을 saveData의 withAddBase2로 반영
        let payloadSaveData = this.saveData;
        if (this.formData.condition === 'MANUFACTURING') {
          const grid = this.currentGrid;
          if (!grid || !grid.gv) {
            this.openPopup('그리드가 초기화되지 않았습니다.');
            return;
          }

          const gv = grid.gv;
          const rowCount = gv.getItemCount();

          // 그리드 데이터에서 고객사원료명칭 -> 제조조성비, 실제조성 매핑 테이블 구성
          const nameToManufacturingRatio = new Map();
          const nameToActualComposition = new Map();
          const nameToManufacturingName = new Map();
          for (let i = 0; i < rowCount; i++) {
            const category = gv.getValue(i, '구분');
            if (category === '합계') continue;
            const name = (gv.getValue(i, '고객사원료명칭') || '').toString().trim();
            if (!name) continue;

            // 제조조성명칭
            const manufacturingName = gv.getValue(i, '제조조성');
            const manufacturingNameStr = manufacturingName == null ? '' : String(manufacturingName);
            if (manufacturingNameStr) {
              nameToManufacturingName.set(name, manufacturingNameStr);
            }

            // 제조조성비 값
            const manufacturingRatioRaw = gv.getValue(i, '제조조성비');
            const manufacturingRatioStr = manufacturingRatioRaw == null ? '' : String(manufacturingRatioRaw).trim();
            if (manufacturingRatioStr) {
              nameToManufacturingRatio.set(name, manufacturingRatioStr);
            }

            // 실제조성 값
            const actualCompositionRaw = gv.getValue(i, '실제조성');
            const actualCompositionStr = actualCompositionRaw == null ? '' : String(actualCompositionRaw).trim();
            if (actualCompositionStr) {
              nameToActualComposition.set(name, actualCompositionStr);
            }
          }

          // 기존 saveData에 실제조성을 withAddBase2로, 제조조성비를 actualComposition으로 반영
          payloadSaveData = (this.saveData || []).map(item => {
            try {
              const category = item.category;
              const material = (item.customerMaterial || '').trim();
              if (!material || !['Solvent', 'Salt', 'Additive'].includes(category)) {
                return item;
              }

              // 제조조성명칭 설정
              if (nameToManufacturingName.has(material)) {
                const v = nameToManufacturingName.get(material);
                item.actualCompositionName = v ? v : null;
              }

              // 제조조성비를 actualComposition에 설정
              if (nameToManufacturingRatio.has(material)) {
                const v = nameToManufacturingRatio.get(material);
                const numeric = parseFloat(String(v).replace(/,/g, ''));
                item.actualComposition = isFinite(numeric) ? String(numeric.toFixed(2)) : null;
              }

              // 실제조성을 withAddBase2에 설정 (그리드의 실제조성 값)
              if (nameToActualComposition.has(material)) {
                const v = nameToActualComposition.get(material);
                const numeric = parseFloat(String(v).replace(/,/g, ''));
                item.withAddBase2 = isFinite(numeric) ? String(numeric.toFixed(2)) : null;
              }

              return item;
            } catch (_) {
              return item;
            }
          });
        }

        // 저장 요청 데이터 구성
        const saveRequest = {
          codeGrade: this.formData.codeGrade,
          condition: this.formData.condition,
          saveData: payloadSaveData,
        };

        console.log('저장 요청 데이터:', saveRequest);

        // 백엔드 저장 API 호출
        const response = await saveMassRatioCalculation(saveRequest);

        if (response && response.data) {
          const { status, data } = response.data;

          if (status === 200 && data && data.isSuccess) {
            // 저장 성공 시 알림창 후 샘플마스터상세 탭 열기
            this.openPopup(data.message || '저장되었습니다.', false, () => {
              this.$emit('sampleMasterDetail', { code: this.formData.codeGrade });
              console.log('close');
              this.close();
            });
          } else {
            this.openPopup(data.message || '저장에 실패했습니다.');
          }
        } else {
          this.openPopup('서버 응답이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error('저장 중 오류:', error);

        // 에러 응답 메시지 처리
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          if (errorData.data && errorData.data.message) {
            this.openPopup(errorData.data.message);
          } else {
            this.openPopup(errorData.errorMessage || errorData.message || '저장 중 오류가 발생했습니다.');
          }
        } else {
          this.openPopup('네트워크 오류가 발생했습니다.');
        }
      }
    },

    // NEW: Solvent 행의 제조조성비 셀 스타일 적용/제거
    setSolventManufacturingCellStyleByName(solventName, makeRed) {
      try {
        const grid = this.currentGrid;
        if (!grid || !grid.gv) return;
        const gv = grid.gv;
        const name = (solventName || '').trim();
        if (!name) return;
        const rowCount = gv.getItemCount();
        for (let i = 0; i < rowCount; i++) {
          const category = gv.getValue(i, '구분');
          const material = gv.getValue(i, '고객사원료명칭');
          if (category === 'Solvent' && String(material || '').trim() === name) {
            if (makeRed) {
              gv.addUserCellStyle(i, '제조조성비', { style: { background: '#ff0000', color: '#ffffff' } });
            } else {
              gv.addUserCellStyle(i, '제조조성비', { style: { background: '#ffffff', color: '#333333' } });
            }
            break;
          }
        }
      } catch (e) {
        console.error('제조조성비 셀 스타일 적용 오류:', e);
      }
    },

    // NEW: 옵션이 존재하는 행을 하늘색으로 표시/해제
    applySkyBlueRowStyle(rowIndex, enable) {
      try {
        const grid = this.currentGrid;
        if (!grid || !grid.gv) return;
        const gv = grid.gv;
        const sky = enable ? { background: '#e6f4ff', color: '#333333' } : { background: '#ffffff', color: '#333333' };
        const fieldsToColor = ['구분', '고객사원료명칭', '사내통용명칭', '조성비', '실제조성', '희석용매', '희석비율', '반제품', '반제품함량', '희석용매함량', '제조조성', '제조조성비'];
        fieldsToColor.forEach(f => {
          try {
            gv.addUserCellStyle(rowIndex, f, { style: sky });
          } catch (_) {}
        });
      } catch (e) {
        console.error('하늘색 로우 스타일 처리 오류:', e);
      }
    },
  },

  mounted() {
    // 초기에는 그리드가 숨겨져 있으므로 헤더 설정을 하지 않음
    // 검색 버튼 클릭 시 그리드 표시와 함께 헤더 설정이 실행됨
  },
};
</script>

<style lang="scss" scoped>
.mass-ratio-calculation {
  width: 100%;
  height: 100%;

  .v-input__slot {
    width: 100% !important;
  }
}

.filter-box {
  margin-bottom: 16px;
}

.filter-list_free {
  display: flex;
  gap: 20px;

  li {
    flex: 1;
    min-width: 200px;

    h4 {
      margin-bottom: 8px;
      font-size: 14px;
      font-weight: 600;
      color: #333;
    }
  }
}

.code-grade-readonly {
  background-color: #f5f5f5;
}

.code-grade-readonly ::v-deep .v-input__slot {
  background-color: #f5f5f5 !important;
}

.content-section {
  margin-top: 20px;
}

.grid-wrapper {
  width: 100%;
  height: 1200px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

/* 구분별 배경색 스타일 */
::v-deep .rg-solvent-row {
  background-color: #ffffff !important; /* 하얀색 */
  color: #333333 !important;
}

::v-deep .rg-salt-row {
  background-color: #e8f5e8 !important; /* 연한 녹색 */
  color: #333333 !important;
}

::v-deep .rg-additive-row {
  background-color: #ffffff !important; /* 하얀색 */
  color: #333333 !important;
}

::v-deep .rg-summary-row {
  background-color: #f5f5f5 !important; /* 연한 회색 */
  color: #333333 !important;
  font-weight: bold !important;
}

/* 그리드 전체 스타일 개선 */
::v-deep .real-grid .rg-header-cell {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: bold;
}

::v-deep .real-grid .rg-cell {
  border-right: 1px solid #e9ecef;
}

/* 구분 컬럼 병합된 셀 스타일 */
::v-deep .real-grid .rg-merged-cell {
  border-right: 1px solid #ddd;
  text-align: center;
  font-weight: bold;
}

/* 행 호버 효과 개선 */
::v-deep .real-grid .rg-row:hover {
  opacity: 0.9;
}

/* 구분 컬럼 텍스트 스타일 */
::v-deep .real-grid [data-field='구분'] {
  font-weight: bold;
  text-align: center;
}

.btn-reset {
  background-color: #f5f5f5 !important;
  color: #666 !important;
}

.btn-search {
  background-color: #08800a !important;
  color: white !important;
}

.btn-excel {
  background-color: #08800a !important;
  color: white !important;
}

.btn-cancel {
  background-color: #f5f5f5 !important;
  color: #666 !important;
  margin-right: 8px !important;
}

.btn-save {
  background-color: #08800a !important;
  color: white !important;
}

// RealGrid 3단 헤더 스타일 커스터마이징
::v-deep .rg-header {
  .rg-header-text {
    font-weight: 600;
    font-size: 12px;
    line-height: 1.2;
  }

  // 구분과 고객사원료명칭 컬럼 특별 스타일
  .rg-header-cell[data-column='구분'],
  .rg-header-cell[data-column='고객사원료명칭'] {
    background-color: #f8f9fa !important;
    font-weight: bold !important;
  }
}

// 그리드 내부 컬럼 그룹 헤더 스타일
::v-deep .rg-column-group-header {
  background-color: #e9ecef;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
}

// 수식 아이콘 이미지 크기 및 간격 조정
::v-deep .rg-header .rg-formula-header {
  display: inline-flex;
  align-items: center;
}
::v-deep .rg-header .rg-formula-header .rg-formula-icon {
  width: 12px;
  height: 12px;
  margin-left: 4px; /* 수식 옆 한 칸 간격 */
  display: inline-block;
}

li {
  display: block !important;
  &:first-child {
    width: 200px !important;
    min-width: 200px !important;
    max-width: 200px !important;
  }

  &:nth-child(2) {
    width: 250px !important;
    min-width: 250px !important;
    max-width: 250px !important;
  }
  h4 {
    margin-bottom: 8px !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    color: #333 !important;
  }
}

// 디버깅 정보 박스 스타일
.no-grid-message {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 14px;
  background-color: #f8f9fa;
  border-radius: 4px;
}
</style>
